import { CheckCircleIcon, ClockIcon } from "@heroicons/react/24/outline";
import {
    formatPhoneNumber,
    TelecomPhoneLineCredentialType,
    TelecomPhoneLineId,
} from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { CreateTelecomPhoneLine } from "components/telecom/create-telecom-phone-line";
import { DeleteButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import {
    GetPersonalPhoneNumbersQuery,
    RentablePhoneNumber,
    useGetPersonalPhoneNumbersQuery,
    useReleasePhoneNumberMutation,
} from "generated/graphql";
import React, { useCallback, useState } from "react";
import { useProductName } from "store/selectors/branding";
import { handleEvent } from "util/user-events";
import { UserTogglePref } from "./account-settings-toggle";

interface PhoneNumberItemProps {
    phoneLine: GetPersonalPhoneNumbersQuery["getPersonalPhoneNumbers"][number];
    onDelete: (id: TelecomPhoneLineId) => void;
    isDeleting: boolean;
}

const PhoneNumberItem: React.FC<PhoneNumberItemProps> = ({
    phoneLine,
    onDelete,
    isDeleting,
}) => {
    const formattedPhoneNumber = formatPhoneNumber(phoneLine.phoneNumber);

    const handleDelete = useCallback(() => {
        onDelete(phoneLine.id);
    }, [onDelete, phoneLine.id]);

    return (
        <div className="space-y-4">
            {phoneLine.credentialType === TelecomPhoneLineCredentialType.Tenant ? (
                <InlineBanner type="info">
                    This phone number was assigned to you by your administrator. If you
                    have questions or need to make changes, please contact your
                    administrator.
                </InlineBanner>
            ) : null}
            <div className="flex items-center space-x-4">
                <div>{formattedPhoneNumber}</div>
                {phoneLine.status === "Provisioning" ? (
                    <div className="flex items-center text-gray-500">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span>Ready in ten minutes or less</span>
                    </div>
                ) : phoneLine.status === "Active" ? (
                    <div className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        <span>Active</span>
                    </div>
                ) : null}
                {phoneLine.credentialType === TelecomPhoneLineCredentialType.Global ? (
                    <DeleteButton
                        backgroundColor="bg-white"
                        confirm={{
                            title: `Release ${formattedPhoneNumber}?`,
                            message:
                                "Are you sure you want to release this phone number? You will no longer be able to use this number for SMS or voice calls.",
                            confirmLabel: "Yes, release it",
                            cancelLabel: "No, keep it",
                        }}
                        onClick={handleDelete}
                    />
                ) : null}
                {isDeleting ? <Spinner /> : null}
            </div>
        </div>
    );
};

export const AccountSettingsPhone: React.FC<unknown> = () => {
    const productName = useProductName();

    const [showCreateUi, setShowCreatePanel] = useState(false);

    const handleCreate = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setShowCreatePanel(true);
    }, []);

    const handleClose = useCallback(() => {
        setShowCreatePanel(false);
    }, []);

    const getPersonalPhoneNumbers = useGetPersonalPhoneNumbersQuery({});
    const releasePhoneNumber = useReleasePhoneNumberMutation();

    const handlePhoneNumberCreated = useCallback(
        (phoneNumber: RentablePhoneNumber) => {
            handleEvent("sms-setup");
            setShowCreatePanel(false);
            getPersonalPhoneNumbers.refetch();
        },
        [getPersonalPhoneNumbers],
    );

    const handleDelete = useCallback(
        async (id: TelecomPhoneLineId) => {
            await releasePhoneNumber.mutateAsync({ id });
            getPersonalPhoneNumbers.refetch();
        },
        [releasePhoneNumber, getPersonalPhoneNumbers],
    );

    return (
        <>
            <div className="space-y-4">
                <ContentFull>
                    <ContentHeader>SMS &amp; Voice Calling</ContentHeader>
                    <ContentSubheader>
                        Send SMS messages and make calls to your contacts directly from{" "}
                        {productName}.
                    </ContentSubheader>
                    <div className="space-y-4">
                        <h3>Personal phone numbers</h3>
                        <QueryRenderer
                            name="AccountSettingsPhone.getPersonalPhoneNumbers"
                            query={getPersonalPhoneNumbers}>
                            {data => {
                                return (
                                    <div className="space-y-4">
                                        {data.getPersonalPhoneNumbers.length === 0 ? (
                                            <>
                                                <InlineBanner type="info">
                                                    No personal phone number has been
                                                    created yet. Reserve a number to get
                                                    started
                                                </InlineBanner>
                                                <PrimaryButton onClick={handleCreate}>
                                                    Reserve phone number
                                                </PrimaryButton>
                                            </>
                                        ) : (
                                            <>
                                                {data.getPersonalPhoneNumbers.map(
                                                    phoneLine => (
                                                        <PhoneNumberItem
                                                            key={phoneLine.id}
                                                            phoneLine={phoneLine}
                                                            onDelete={handleDelete}
                                                            isDeleting={
                                                                releasePhoneNumber.isLoading
                                                            }
                                                        />
                                                    ),
                                                )}
                                                <UserTogglePref
                                                    field="useProvisionedPhoneOnIntakePdf"
                                                    label="Show your in-app phone number on the client intake PDF"
                                                    description="Your in-app phone number will appear instead of your personal phone number on client intake PDFs."
                                                />
                                            </>
                                        )}
                                    </div>
                                );
                            }}
                        </QueryRenderer>
                    </div>
                </ContentFull>
            </div>
            {showCreateUi ? (
                <Panel
                    isOpen={showCreateUi}
                    type={PanelType.large}
                    headerText="Reserve phone number"
                    onDismiss={handleClose}>
                    <CreateTelecomPhoneLine onCreated={handlePhoneNumberCreated} />
                </Panel>
            ) : null}
        </>
    );
};
